import { useCallback, useEffect, useMemo, useState } from "react";

import { handlePromise } from "../../../utils/handlePromisse";
import { getPatientsPaginated2 } from "../../../services/patient.service";
import { deleteConsult } from "../../../services/history";
import { deleteConsultOral } from "../../../services/oralService";
import { filterConsultations, flattenConsultations, groupByPatient, HistoryConsultation, HistorySortOrder } from "./history-filters";

const HISTORY_SORT_STORAGE_KEY = "history-sort-order";

const isValidSortOrder = (value: string | null): value is HistorySortOrder =>
  value === "alphabetical" || value === "last-consultation" || value === "consultation-count";

const readStoredSortOrder = (): HistorySortOrder => {
  try {
    const stored = sessionStorage.getItem(HISTORY_SORT_STORAGE_KEY);
    if (isValidSortOrder(stored)) {
      return stored;
    }
  } catch {}

  return "last-consultation";
};

const persistSortOrder = (sortOrder: HistorySortOrder) => {
  try {
    sessionStorage.setItem(HISTORY_SORT_STORAGE_KEY, sortOrder);
  } catch {}
};

export default function useConsultationsHistory() {
  const [items, setItems] = useState<HistoryConsultation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<HistorySortOrder>(readStoredSortOrder);

  useEffect(() => {
    persistSortOrder(sortOrder);
  }, [sortOrder]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [result, fetchError] = await handlePromise(getPatientsPaginated2({ page: 1, size: 1000 }));
    setLoading(false);
    if (fetchError) {
      setError("Erro ao carregar o histórico.");
      return;
    }
    setItems(flattenConsultations(result.data.data ?? []));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => filterConsultations(items, query), [items, query]);
  const groups = useMemo(() => groupByPatient(filtered, sortOrder), [filtered, sortOrder]);

  const deleteConsultation = useCallback(
    async (item: HistoryConsultation): Promise<{ ok: boolean; message?: string }> => {
      const request = item.type === "ORAL" ? deleteConsultOral(item.id) : deleteConsult(item.id);
      const [, requestError] = await handlePromise(request);
      if (requestError) {
        const message: string | undefined = (requestError as any)?.response?.data?.message;
        return { ok: false, message };
      }
      await load();
      return { ok: true };
    },
    [load]
  );

  return {
    items,
    filtered,
    groups,
    loading,
    error,
    query,
    setQuery,
    sortOrder,
    setSortOrder,
    reload: load,
    deleteConsultation,
  };
}
